import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import useStateManager from 'components/hooks/useStateManager';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import DomainPerformanceGraph from '../ProficiencyGraph/DomainPerformanceGraph';
import ProficiencyPlacementExam from '../ProficiencyGraph/ProficiencyPlacementExam';
import { useDispatch } from 'react-redux';
import dayjs from 'utils/dayjsConfig';
import { handleListExamGraphData } from 'store/organizations/thunks';
import {
  handleGetDomainPerformanceGraphData,
  handleGetSubmissionStatusProficiency,
  handleListCourseInfo,
} from 'store/standardexams';
import ContentLoading from 'modules/standardExamReport/contentLoading';
import { useExams } from 'store/exams/selectors';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import { groupBy } from 'lodash';
import ProficiecnyExamStack from './ProficiecnyExamStack';
import { useCurrentOrg } from 'store/organizations/selectors';
import ExamReportSupport from '../ExamReportSupport';
import { useCurrentUser } from 'store/users/selectors';
import { useCurrentClass } from 'store/classes/selectors';
import { useUserClasses } from 'store/userClasses/selectors';
import { handleGetClass } from 'store/classes/thunks';

const years = new Date().getFullYear();
const upcomming_year = `${years}-${years + 1}`;
const downcomming_year = `${years - 1}-${years}`;

const school_level = [
  { value: 'high', label: 'HIGH SCHOOL' },
  { value: 'middle', label: 'MIDDLE SCHOOL' },
];

const proficiencyExamDetails = [
  { title: 'Juan Diaz', school: 'Mountain Elementary', proprity: 'High', priorityColor: '#df0d14' },
  { title: 'Claudia Martínez', school: 'Memorial High School', proprity: 'Mid', priorityColor: '#7b13c3' },
  { title: 'Maria Marco', school: 'Mountain Elementary', proprity: 'Low', priorityColor: '#078247' },
];

const flagDataProficiencyExam = [
  { title: 'Red = Urgent Support (high 50% failing, downward trend).', backgroundcolor: 'linear-gradient(180deg,#ef476f,#d7265e)' },
  { title: 'Orange = Needs Attention (moderate 25% failing, stagnant growth).', backgroundcolor: 'linear-gradient(180deg, #FFB703, #fb742b)' },
  { title: 'Green = On Track (80% passing  majority, positive trend).', backgroundcolor: 'linear-gradient(180deg, #03989e, #03989e)' },
];

const ReportHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
  }
`;

const OverviewText = styled.span`
  color: black;
  font-size: 22px;
  font-weight: 800;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  line-height: 1.25;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
    font-size: 18px;
  }
`;

const TitleAccent = styled.span`
  color: #fb742b;
  font-size: 22px;
  font-weight: 900;
  white-space: nowrap;

  @media (max-width: 767px) {
    font-size: 18px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
    flex-wrap: nowrap;
    justify-content: flex-end;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const SessionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 767px) {
    width: 100%;
    flex: 1 1 100%;
  }
`;

const ActionButtonLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  margin-left: 5px;
  color: #000;
`;

const ReportPageWrapper = styled.div`
  margin-top: 20px;
  padding-bottom: 60px;

  @media (max-width: 1024px) {
    padding-bottom: 80px;
  }
`;

const filterFormControlSx = {
  minWidth: { xs: '100%', sm: 200 },
  maxWidth: { xs: '100%', sm: 300 },
  width: { xs: '100%', sm: 'auto' },
  flex: { xs: '1 1 100%', sm: '0 1 auto' },
  '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' },
  '& .MuiSelect-select': { py: 1.25, px: 1.5, fontSize: '14px', minHeight: 20 },
  '& .MuiInputLabel-outlined': { fontSize: '14px', fontWeight: 600 },
};

const TeacherProficiencyReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const currentClass = useCurrentClass();
  const userClasses = useUserClasses();
  const currentOrg = useCurrentOrg();
  const showAlert = useAlert();
  const exams = useExams();
  const currentYearSession = useMemo(() => (dayjs().month() > 7 ? upcomming_year : downcomming_year));
  const sessionOptions = useMemo(() => {
    const unique = Array.from(new Set((exams || []).map((e) => e.session))).filter(Boolean);
    return unique.length ? unique : [currentYearSession];
  }, [exams, currentYearSession]);
  const [activeSession, setActiveSession] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(() => location.state?.selectedClassId || '');
  const [classChangeLoading, setClassChangeLoading] = useState(false);
  const [states, updateStates] = useStateManager({
    result: {},
    loading: {},
    filterRecords: {
      domainPerformanceGraph: { exam_type: '1', school_level: 'high' },
      placementExamResult: { exam_type: '1', school_level: 'high' },
    },
    optionsData: { school_level: school_level || [], exam_type: [] },
  });
  const [constentLoading, setContentLoader] = useState(false);
  const [assignmentStatusCount, setAssignmentStatusCount] = useState({
    examTaken: 0,
    notStarted: 0,
    notFinished: 0,
    notAssigned: 0,
  });

  const [examReportSupportData, setExamReportSupportData] = useState([]);
  const [examReportSupportRefreshKey, setExamReportSupportRefreshKey] = useState(0);
  const [isRefreshingReport, setIsRefreshingReport] = useState(false);
  const classLoadRequestRef = useRef(0);
  const classOptions = useMemo(
    () => (userClasses || []).filter((item) => !item?.archived),
    [userClasses]
  );

  useEffect(() => {
    if (selectedClassId) return;
    if (currentClass?.id) {
      setSelectedClassId(currentClass.id);
    } else if (classOptions.length > 0) {
      setSelectedClassId(classOptions[0]?.id || '');
    }
  }, [currentClass?.id, classOptions, selectedClassId]);

  const effectiveClass = useMemo(
    () => (selectedClassId ? { ...currentClass, id: selectedClassId } : currentClass),
    [currentClass, selectedClassId]
  );

  const excelBorderThin = {
    top: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    bottom: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    left: { style: 'thin', color: { argb: 'FFB0BEC5' } },
    right: { style: 'thin', color: { argb: 'FFB0BEC5' } },
  };

  const hexToArgbFont = (hex) => {
    if (!hex || typeof hex !== 'string') return 'FF37474F';
    const normalized = hex.replace('#', '').trim();
    if (normalized.length === 6) {
      return `FF${normalized.toUpperCase()}`;
    }
    return 'FF37474F';
  };

  const downloadExamReportSupportCSV = async () => {
    try {
      if (!examReportSupportData || examReportSupportData.length === 0) {
        showAlert('No data available for download', 'warning');
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const { saveAs } = await import('file-saver');
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Teachers Needing Support', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      sheet.columns = [
        { width: 28 },
        { width: 26 },
        { width: 20 },
        { width: 20 },
        { width: 16 },
      ];

      const headerRow = sheet.addRow([
        'Teacher Name',
        'School',
        'Result',
        'No. of Assignments',
        'Average Grade',
      ]);
      headerRow.height = 22;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF02796D' },
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 12 };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.border = excelBorderThin;
      });

      examReportSupportData.forEach((item, dataIdx) => {
        const row = sheet.addRow([
          item.title || '-',
          item.school || item.schoolName || '-',
          item.proprity || '-',
          item.noOfAssignments || item.uniqueAssignments || 0,
          item.averageGrade ?? 0,
        ]);

        row.height = 20;
        const rowFill = dataIdx % 2 === 0 ? 'FFFFFFFF' : 'FFF5F5F5';
        const resultArgb = hexToArgbFont(item.priorityColor);

        row.eachCell((cell, colNumber) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: rowFill },
          };
          cell.font = { size: 11, color: { argb: 'FF263238' } };
          cell.border = excelBorderThin;
          cell.alignment = { vertical: 'middle', wrapText: true };

          if (colNumber === 3) {
            cell.font = { bold: true, size: 11, color: { argb: resultArgb } };
          }
          if (colNumber === 4 || colNumber === 5) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        `teachers_needing_support_${dayjs().format('DD-MM-YY_hh-mm-ss')}.xlsx`
      );
    } catch (error) {
      showAlert('Error downloading report', 'error');
    }
  };

  useEffect(() => {
    if (exams && activeSession == null) {
      const grp = groupBy(exams, 'session');
      if (grp[upcomming_year]) {
        if (activeSession != upcomming_year) setActiveSession(upcomming_year);
      } else if (grp[downcomming_year]) {
        if (activeSession != downcomming_year) setActiveSession(downcomming_year);
      }
    }
  }, [exams, activeSession]);

  const exam_type = useMemo(() => {
    if (exams.length > 0) {
      const filterbasedontype = exams.filter(
        (exam) =>
          exam.exam_school === states?.filterRecords?.domainPerformanceGraph?.school_level &&
          exam.session === activeSession
      );
      if (filterbasedontype.length > 0 && filterbasedontype[0]?.exams) {
        return filterbasedontype[0].exams.map((exam) => ({
          value: String(exam.id),
          label: exam.displayName,
        }));
      }
    }
    return [];
  }, [exams, states?.filterRecords?.domainPerformanceGraph?.school_level, activeSession]);

  const placement_exam_type = useMemo(() => {
    if (exams.length > 0) {
      const filterbasedontype = exams.filter(
        (exam) =>
          exam.exam_school === states?.filterRecords?.placementExamResult?.school_level &&
          exam.session === activeSession
      );
      if (filterbasedontype.length > 0 && filterbasedontype[0]?.exams) {
        return filterbasedontype[0].exams.map((exam) => ({
          value: String(exam.id),
          label: exam.displayName,
        }));
      }
    }
    return [];
  }, [exams, states?.filterRecords?.placementExamResult?.school_level, activeSession]);

  useEffect(() => {
    if (exam_type.length > 0) {
      updateStates((prevState) => ({
        ...prevState,
        optionsData: {
          ...prevState.optionsData,
          exam_type: exam_type,
          placement_exam_type: placement_exam_type,
        },
      }));
    }
  }, [exam_type, placement_exam_type, updateStates]);

  const dispatch = useDispatch();

  const fetchInitalData = useCallback(async () => {
    if (!currentOrg?.id) return;
    try {
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: true },
      }));

      const courseInfoResult = await dispatch(handleListCourseInfo({ organisationId: currentOrg.id }));

      const result = {
        ...(courseInfoResult?.data ? { courseInfo: courseInfoResult?.data } : {}),
      };
      const optionsData = {
        ...(courseInfoResult?.data ? { courseInfo: courseInfoResult?.data } : {}),
      };
      updateStates((prevState) => ({
        ...prevState,
        result: { ...prevState.result, ...result },
        optionsData: { ...prevState.optionsData, ...optionsData },
      }));
    } catch (error) {
      console.log(error, 'error in fetching initial data');
    } finally {
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: false },
      }));
    }
  }, [currentOrg?.id, updateStates, dispatch]);

  const handleOnChangeProficiencyPlacement = async (value, classIdOverride = null) => {
    try {
      const classIdToUse = classIdOverride || selectedClassId || currentClass?.id;
      const payload = {
        id: currentOrg?.parentId,
        session: activeSession || currentYearSession,
        exam_type: value.exam_type,
        school_level: value.school_level,
        teacher_id: currentUser?.id,
        class_id: classIdToUse,
      };
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: true },
        filterRecords: {
          ...prevState.filterRecords,
          placementExamResult: {
            exam_type: value?.exam_type || '',
            school_level: value?.school_level || '',
          },
        },
      }));
      const result = await dispatch(handleListExamGraphData(payload));
      if (result?.data) {
        updateStates((prevState) => ({
          ...prevState,
          result: { ...prevState.result, placementExamResult: result?.data || {} },
        }));
      }
    } catch (error) {
      console.log(error, 'error in handling proficiency placement');
    } finally {
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: false },
      }));
    }
  };

  const handleOnChangeDomainPerformanceGraph = async (value, classIdOverride = null) => {
    try {
      const classIdToUse = classIdOverride || selectedClassId || currentClass?.id;
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: true },
      }));
      const findSchoolType = exams.find(
        (exam) =>
          exam.exam_school === value?.school_level &&
          exam.session === (activeSession || currentYearSession)
      );
      console.log(findSchoolType, 'findSchoolType');
      const bookIDs = findSchoolType
        ? findSchoolType?.exams[Number(value?.exam_type) - 1]?.bookId
        : [];
      const payload = {
        org_id: currentOrg?.parentId,
        session: activeSession || currentYearSession,
        school_type: value?.school_level,
        book_ids: [bookIDs] || [],
        teacher_id: currentUser?.id,
        class_id: classIdToUse,
      };
      console.log(bookIDs, 'bookIDs');
      // const result = await dispatch(handleGetDomainPerformanceGraphData(payload));
      // if (result?.status_code === 404) {
      //   showAlert('No submissions data found', 'error');
      // }
      // if (result?.data) {
      //   updateStates((prevState) => ({
      //     ...prevState,
      //     loading: { ...prevState.loading, initial: false },
      //     filterRecords: { ...prevState.filterRecords, domainPerformanceGraph: value },
      //     result: { ...prevState.result, domainPerformanceGraphData: result?.data || {} },
      //   }));
      // }
    } catch (error) {
      console.log(error, 'error in handling domain performance graph');
    } finally {
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: false },
      }));
    }
  };

  const handleOnChangeStandardExamTaken = async (value, classIdOverride = null, loadRequestId = null) => {
    try {
      const classIdToUse = classIdOverride || selectedClassId || currentClass?.id;
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: true },
        filterRecords: {
          ...prevState.filterRecords,
          selected_proficiency_exam_taken: value,
        },
      }));
      let result = null;
      if (!value || value === 'All') {
        result = await dispatch(
          handleGetSubmissionStatusProficiency(
            currentOrg.parentId,
            activeSession || currentYearSession,
            null,
            null,
            currentUser?.id,
            classIdToUse,
          )
        );
      } else {
        result = await dispatch(
          handleGetSubmissionStatusProficiency(
            currentOrg.parentId,
            activeSession || currentYearSession,
            value,
            null,
            currentUser?.id,
            classIdToUse,
          )
        );
      }
      if (result?.success) {
        if (loadRequestId != null && loadRequestId !== classLoadRequestRef.current) return;
        const { examTaken, notStarted, notFinished, notAssigned } = result?.data || {};
        setAssignmentStatusCount({
          examTaken: examTaken || 0,
          notStarted: notStarted || 0,
          notFinished: notFinished || 0,
          notAssigned: notAssigned || 0,
        });
      }
      if (result?.status_code === 200) {
        updateStates((prevState) => ({
          ...prevState,
          loading: { ...prevState.loading, initial: false },
        }));
      }
    } catch (error) {
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: false },
      }));
      showAlert('Error in handling change standard exam taken', 'error');
    } finally {
      updateStates((prevState) => ({
        ...prevState,
        loading: { ...prevState.loading, initial: false },
      }));
    }
  };

  const handleOnChangeClass = (classId) => {
    if (classId && classId !== selectedClassId) {
      setSelectedClassId(classId);
    }
  };

  useEffect(() => {
    if (!currentOrg?.id || !activeSession) return;
    fetchInitalData();
  }, [currentOrg?.id, activeSession, fetchInitalData]);

  useEffect(() => {
    if (!selectedClassId || !activeSession || !currentOrg?.id) return;

    const requestId = ++classLoadRequestRef.current;
    setClassChangeLoading(true);

    const loadClassReport = async () => {
      try {
        const selectedExamTaken = states?.filterRecords?.selected_proficiency_exam_taken || 'All';
        await handleOnChangeStandardExamTaken(selectedExamTaken, selectedClassId, requestId);
      } catch (error) {
        if (requestId === classLoadRequestRef.current) {
          showAlert('Error changing class', 'error');
        }
      } finally {
        if (requestId === classLoadRequestRef.current) {
          setClassChangeLoading(false);
        }
      }
    };

    loadClassReport();
  }, [selectedClassId, activeSession, currentOrg?.id]);

  const handleRefreshReport = async () => {
    if (!currentOrg?.id || classChangeLoading || isRefreshingReport) return;
    setIsRefreshingReport(true);
    try {
      setExamReportSupportRefreshKey((k) => k + 1);
      await fetchInitalData();
      const selectedExamTaken = states?.filterRecords?.selected_proficiency_exam_taken || 'All';
      await handleOnChangeStandardExamTaken(selectedExamTaken, selectedClassId || currentClass?.id);
      await Promise.all([
        handleOnChangeDomainPerformanceGraph(states?.filterRecords?.domainPerformanceGraph, selectedClassId || currentClass?.id),
        handleOnChangeProficiencyPlacement(states?.filterRecords?.placementExamResult, selectedClassId || currentClass?.id),
      ]);
    } catch (error) {
      showAlert('Unable to refresh report', 'error');
    } finally {
      setIsRefreshingReport(false);
    }
  };

  const handleBack = async () => {
    const { returnTo, openGradebookExam, examTab } = location.state || {};
    const classIdToReturn = selectedClassId || currentClass?.id;
    if (classIdToReturn && classIdToReturn !== currentClass?.id) {
      await dispatch(handleGetClass(classIdToReturn));
    }
    if (returnTo && openGradebookExam) {
      navigate(returnTo, {
        state: {
          openGradebookExam: true,
          examTab: examTab || 'exam1',
          selectedClassId: classIdToReturn,
        },
      });
      return;
    }
    if (currentUser?.role === 'teacher') {
      navigate('/admin/class?view=gradebook', {
        state: {
          openGradebookExam: true,
          examTab: 'exam1',
          selectedClassId: classIdToReturn,
        },
      });
      return;
    }
    navigate(-1);
  };

  return (
    <ReportPageWrapper>
      <ContentLoading isLoading={states.loading.initial || constentLoading} />
      <div className="flex flex-col gap-20">
        <ReportHeader>
          <OverviewText>
            1WAY&nbsp;
            <TitleAccent>PROFICIENCY REPORT</TitleAccent>
          </OverviewText>
          <HeaderActions>
            <ActionButtons>
              <IconButton size="small" onClick={handleBack} aria-label="Back">
                <ArrowBack fontSize="small" style={{ color: '#000' }} />
                <ActionButtonLabel>Back</ActionButtonLabel>
              </IconButton>
              <Tooltip title="Refresh report">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleRefreshReport}
                    disabled={!currentOrg?.id || classChangeLoading || isRefreshingReport}
                    aria-label="Refresh report"
                  >
                    <Refresh fontSize="small" style={{ color: '#000' }} />
                    <ActionButtonLabel>Refresh</ActionButtonLabel>
                  </IconButton>
                </span>
              </Tooltip>
            </ActionButtons>
            <SessionGroup>
              <FormControl variant="outlined" size="medium" sx={filterFormControlSx}>
                <InputLabel id="tpr-session-label">Session</InputLabel>
                <Select
                  labelId="tpr-session-label"
                  id="tpr-session-select"
                  label="Session"
                  value={activeSession || ''}
                  onChange={(e) => setActiveSession(e.target.value)}
                >
                  {(sessionOptions || []).map((s) => (
                    <MenuItem key={s} value={s} sx={{ fontSize: '14px' }}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </SessionGroup>
          </HeaderActions>
        </ReportHeader>

        <div className="flex flex-row gap-20 flex-wrap items-center ">
          <Box
            sx={{
              width: '100%',
              border: '1px solid #e8e8e8',
              borderRadius: '12px',
              padding: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
                width: '100%',
              }}
            >
              {classChangeLoading ? <CircularProgress size={22} thickness={5} /> : null}
              <FormControl variant="outlined" size="medium" sx={filterFormControlSx}>
                <InputLabel id="tpr-classes-label">Classes</InputLabel>
                <Select
                  labelId="tpr-classes-label"
                  id="tpr-classes-select"
                  label="Classes"
                  value={selectedClassId || ''}
                  displayEmpty
                  disabled={classChangeLoading}
                  onChange={(e) => handleOnChangeClass(e.target.value)}
                  renderValue={(val) => {
                    if (!val) return '';
                    const selectedClass = classOptions.find((item) => item?.id === val);
                    return selectedClass?.displayName || '';
                  }}
                >
                  {classOptions.map((item) => (
                    <MenuItem key={item?.id} value={item?.id} sx={{ fontSize: '14px' }}>
                      {item?.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="medium" sx={filterFormControlSx}>
                <InputLabel id="tpr-course-label">Course</InputLabel>
                <Select
                  labelId="tpr-course-label"
                  id="tpr-course-select"
                  label="Course"
                  value={states?.filterRecords?.selected_proficiency_exam_taken || 'All'}
                  displayEmpty
                  onChange={(e) => handleOnChangeStandardExamTaken(e.target.value, selectedClassId || currentClass?.id)}
                  renderValue={(val) => {
                    if (val === 'All' || !val) return 'All';
                    const checkData = (states?.optionsData?.courseInfo || []).find(
                      (item) => item?.id === val
                    );
                    return checkData?.title || 'All';
                  }}
                >
                  <MenuItem value="All" sx={{ fontSize: '14px' }}>
                    <em>All</em>
                  </MenuItem>
                  {(states?.optionsData?.courseInfo || []).map((item) => (
                    <MenuItem key={item?.id} value={item?.id} sx={{ fontSize: '14px' }}>
                      {item?.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={assignmentStatusCount?.examTaken || 0}
                  label="Proficiency Exam"
                  subLabel="Taken"
                  backgroundColor="linear-gradient(90deg, #0B0B0B 0%, #2B2A9F 60%, #3533CD 100%)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={assignmentStatusCount?.notStarted || 0}
                  label="Proficiency Exam"
                  subLabel="Not Started"
                  backgroundColor="linear-gradient(90deg, #ff3131, #ff914d)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={assignmentStatusCount?.notFinished || 0}
                  label="Proficiency Exam"
                  subLabel="Not Finished"
                  backgroundColor="linear-gradient(90deg, #8c52ff, #00bf63)"
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
                <ProficiecnyExamStack
                  value={assignmentStatusCount?.notAssigned || 0}
                  label="Proficiency Exam"
                  subLabel="Not Assigned"
                  backgroundColor="linear-gradient(90deg, #004aad, #cb6ce6)"
                />
              </Grid>
            </Grid>
          </Box>
        </div>

        <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
          <Grid size={{ xs: 12, md: 12, lg: 5 }} sx={{ border: '1px solid #e8e8e8', borderRadius: '20px' }} item>
            <ExamReportSupport
              key={`${examReportSupportRefreshKey}-${selectedClassId || ''}`}
              redirectTo="/proficiency-report"
              from="proficiency"
              examDetails={proficiencyExamDetails}
              flagData={flagDataProficiencyExam}
              activeSession={activeSession}
              selectedClassId={selectedClassId}
              flagEyeModal="proficiencyExamModalTeacher"
              onDataLoaded={setExamReportSupportData}
              onDownload={downloadExamReportSupportCSV}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 12, lg: 7 }} sx={{ width: '100%', m: 0, border: '1px solid #e8e8e8', borderRadius: '20px' }}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: 0,
                borderRadius: '12px',
                padding: '12px',
              }}
            >
              <Grid container sx={{ width: '100%', m: 0 }}>
                <Grid
                  item
                  size={{ xs: 12, md: 12, lg: 6 }}
                  sx={{
                    borderRight: { xs: 'none', md: '2px solid #e8e8e8' },
                    paddingRight: { xs: 0, md: '20px' },
                  }}
                >
                  <DomainPerformanceGraph
                    key={`domain-graph-${selectedClassId}`}
                    rows={states?.result?.domainPerformanceGraphData || {}}
                    filterRecords={states?.filterRecords?.domainPerformanceGraph}
                    onChangeDomainPerformanceGraph={handleOnChangeDomainPerformanceGraph}
                    optionsData={{
                      school_level: states?.optionsData?.school_level || [],
                      exam_type: states?.optionsData?.exam_type || [],
                    }}
                    activeSession={activeSession}
                    exams={exams}
                    currentUser={currentUser}
                    currentClass={effectiveClass}
                  />
                </Grid>
                <Grid
                  item
                  size={{ xs: 12, md: 12, lg: 6 }}
                  sx={{ paddingLeft: { xs: 0, md: '20px' } }}
                >
                  <ProficiencyPlacementExam
                    key={`placement-graph-${selectedClassId}`}
                    onChangeProficiency={handleOnChangeProficiencyPlacement}
                    filterRecords={states?.filterRecords?.placementExamResult}
                    optionsData={{
                      school_level: states?.optionsData?.school_level || [],
                      exam_type: states?.optionsData?.placement_exam_type || [],
                    }}
                    activeSession={activeSession}
                    exams={exams}
                    currentUser={currentUser}
                    currentClass={effectiveClass}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </div>
    </ReportPageWrapper>
  );
};

export default TeacherProficiencyReport;
